// Persistent battlefield decals (roadmap #5): bomb craters and burning crash
// sites on land, oil slicks at sea. They are rendered ON TOP of the pristine
// terrain — the server never mutates the shared heightfield — from a round-lived
// list fed by the welcome handshake and one-shot `scar` events, and wiped on
// roundStart. Only the crash fires cost anything per frame, and those are
// LOD-gated to the camera's neighbourhood.

import * as THREE from "three";
import { mergeBoxes } from "./planes.js";

export class Scars {
  constructor(scene, field, waterY, particles) {
    this.field = field;
    this.waterY = waterY;
    this.particles = particles;
    this.group = new THREE.Group();
    scene.add(this.group);
    this.scars = new Map(); // id -> { kind, x, y, z, r, mesh, fireAt }
    this.time = 0;
  }

  clear() {
    for (const s of this.scars.values()) {
      s.mesh.traverse((o) => o.geometry?.dispose?.());
      this.group.remove(s.mesh);
    }
    this.scars.clear();
  }

  setFromWelcome(list) {
    this.clear();
    for (const s of list ?? []) this.add(s.id, s.k, s.x, s.y, s.z, s.r);
  }

  add(id, kind, x, y, z, r) {
    if (this.scars.has(id) || !(r > 0)) return;
    const mesh = kind === 2 ? this._oil(x, z, r) : kind === 1 ? this._wreck(x, z, r) : this._crater(x, z, r);
    this.group.add(mesh);
    this.scars.set(id, { kind, x, y, z, r, mesh, fireAt: 0 });
  }

  // Ground height, floored at the sea so a crater on the shore never sinks.
  _groundY(x, z) {
    return Math.max(this.waterY, this.field.surfaceAt(x, z));
  }

  // A crater: a broad scorched char patch, a scatter of blast marks, and a
  // raised dirt rim — every box laid on the terrain it sits over, 0.15 m proud
  // to avoid z-fighting. Reads as deformation without touching the heightfield.
  _crater(cx, cz, r) {
    const boxes = [{ x: 0, y: this._groundY(cx, cz) + 0.16, z: 0, w: r * 1.35, h: 0.12, d: r * 1.35, color: 0x1c1813 }];
    const scatter = 10 + Math.round(r);
    for (let i = 0; i < scatter; i++) {
      const a = Math.random() * Math.PI * 2;
      const rr = Math.sqrt(Math.random()) * r;
      const dx = Math.cos(a) * rr, dz = Math.sin(a) * rr;
      const s = 0.5 + Math.random() * r * 0.3;
      boxes.push({ x: dx, y: this._groundY(cx + dx, cz + dz) + 0.18, z: dz, w: s, h: 0.1, d: s, color: i % 3 ? 0x2c2822 : 0x3a352c });
    }
    const rim = 12;
    for (let i = 0; i < rim; i++) {
      const a = (i / rim) * Math.PI * 2 + Math.random() * 0.2;
      const dx = Math.cos(a) * r * 1.05, dz = Math.sin(a) * r * 1.05;
      boxes.push({ x: dx, y: this._groundY(cx + dx, cz + dz) + 0.42, z: dz, w: r * 0.4, h: 0.85, d: r * 0.4, color: i % 2 ? 0x6b5c46 : 0x554836 });
    }
    const mesh = new THREE.Mesh(mergeBoxes(boxes), new THREE.MeshLambertMaterial({ vertexColors: true }));
    mesh.position.set(cx, 0, cz);
    return mesh;
  }

  // A crash site: a charred fuselage stub and torn wing root on a scorched
  // patch. The looping fire/smoke is emitted in update(), LOD-gated.
  _wreck(cx, cz, r) {
    const gy = this._groundY(cx, cz);
    const mesh = new THREE.Mesh(mergeBoxes([
      { x: 0, y: gy + 0.05, z: 0, w: r * 1.6, h: 0.1, d: r * 1.6, color: 0x241f18 }, // scorched ground
      { x: 0, y: gy + 0.45, z: 0, w: 3.2, h: 0.8, d: 1.0, color: 0x141110 },        // fuselage stub
      { x: 1.5, y: gy + 0.6, z: 0, w: 1.0, h: 0.95, d: 0.7, color: 0x1c1813 },       // engine block
      { x: -1.1, y: gy + 0.32, z: 0.5, w: 1.3, h: 0.4, d: 1.7, color: 0x1a1712 },    // torn wing root
    ]), new THREE.MeshLambertMaterial({ vertexColors: true }));
    mesh.position.set(cx, 0, cz);
    mesh.rotation.y = (cx * 0.13 + cz * 0.19) % (Math.PI * 2); // varied wreck heading
    mesh.userData.fireY = gy + 0.9;
    return mesh;
  }

  // An oil slick: a dark low-opacity disc on the water with a little drifting
  // flotsam.
  _oil(cx, cz, r) {
    const g = new THREE.Group();
    const disc = new THREE.Mesh(
      new THREE.CircleGeometry(r, 30).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ color: 0x090a0c, transparent: true, opacity: 0.52, depthWrite: false }),
    );
    disc.position.y = this.waterY + 0.1;
    g.add(disc);
    const boxes = [];
    const n = 5 + Math.round(r / 8);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2, rr = Math.random() * r * 0.8;
      const s = 0.4 + Math.random() * 0.8;
      boxes.push({ x: Math.cos(a) * rr, y: this.waterY + 0.22, z: Math.sin(a) * rr, w: s, h: 0.25, d: s, color: i % 2 ? 0x2a2d33 : 0x161513 });
    }
    g.add(new THREE.Mesh(mergeBoxes(boxes), new THREE.MeshLambertMaterial({ vertexColors: true })));
    g.position.set(cx, 0, cz);
    return g;
  }

  update(dt, cameraPos) {
    this.time += dt;
    for (const s of this.scars.values()) {
      if (s.kind !== 1) continue; // only crash fires burn
      const dx = s.x - cameraPos.x, dz = s.z - cameraPos.z;
      if (dx * dx + dz * dz > 1500 * 1500) continue; // LOD: within 1.5 km
      s.fireAt -= dt;
      if (s.fireAt <= 0) {
        s.fireAt = 0.5; // ~2 Hz
        const fy = s.mesh.userData.fireY ?? s.y;
        this.particles.fire(new THREE.Vector3(s.x + (Math.random() - 0.5) * 2, fy, s.z + (Math.random() - 0.5) * 2));
        this.particles.smoke(new THREE.Vector3(s.x + (Math.random() - 0.5) * 2, fy + 1, s.z + (Math.random() - 0.5) * 2));
      }
    }
  }
}
